import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Scale, Users, IndianRupee, FileBarChart,
  ClipboardList, Wallet, Database, LogOut, Flame, Menu, FileText, Boxes, Receipt, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["owner", "office"] },
  { to: "/reconciliation", label: "Reconciliation", icon: Scale, roles: ["owner"] },
  { to: "/cylinder-stock", label: "Cylinder Stock", icon: Boxes, roles: ["owner"] },
  { to: "/transactions", label: "Transactions", icon: Receipt, roles: ["owner"] },
  { to: "/office", label: "Office Entries", icon: ClipboardList, roles: ["office"] },
  { to: "/invoices", label: "HP Invoices", icon: FileText, roles: ["owner", "office"] },
  { to: "/cash", label: "Cash Confirm", icon: Wallet, roles: ["office"] },
  { to: "/consumers", label: "Consumers", icon: Database, roles: ["office"] },
  { to: "/reports", label: "Reports", icon: FileBarChart, roles: ["owner", "office"] },
  { to: "/prices", label: "Prices", icon: IndianRupee, roles: ["owner", "office"] },
  { to: "/users", label: "Users", icon: Users, roles: ["owner"] },
  { to: "/billing", label: "Billing", icon: CalendarClock, roles: ["admin"] },
];

const NavList = ({ role, onNav }) => (
  <nav className="flex flex-col gap-1 p-3">
    {NAV.filter((n) => n.roles.includes(role)).map((n) => (
      <NavLink
        key={n.to}
        to={n.to}
        onClick={onNav}
        data-testid={`nav-${n.to.replace("/", "")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
            isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
          }`
        }
      >
        <n.icon className="h-4 w-4" />
        {n.label}
      </NavLink>
    ))}
  </nav>
);

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const Brand = (
    <div className="flex items-center gap-2 px-4 py-5 border-b">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
        <Flame className="h-5 w-5" />
      </div>
      <div>
        <div className="font-head text-lg font-bold leading-none">Anila</div>
        <div className="text-[11px] text-muted-foreground">Gas Agency Control</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 border-r bg-card">
        {Brand}
        <NavList role={user.role} />
        <div className="mt-auto p-3 border-t">
          <div className="px-3 py-2 text-sm">
            <div className="font-medium">{user.name}</div>
            <div className="text-xs text-muted-foreground capitalize">{user.role}</div>
          </div>
          <Button variant="outline" className="w-full justify-start gap-2" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="md:pl-64">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b bg-card/95 backdrop-blur px-4 h-14 md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64">
              {Brand}
              <NavList role={user.role} />
              <div className="p-3 border-t">
                <Button variant="outline" className="w-full justify-start gap-2" onClick={logout}>
                  <LogOut className="h-4 w-4" /> Sign out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-primary" />
            <span className="font-head font-bold">Anila</span>
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">{children}</main>
      </div>
    </div>
  );
};
